import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Switch,
  useTheme,
} from '@mui/material'
import { BorderColor, Delete, MoreVert } from '@mui/icons-material'
import { StyledMenu } from '../../styles/PopupStyles'
import MenuItem from '@mui/material/MenuItem'
import { useEditMode, useNotification } from '../../store/redux/hooks'
import { t } from 'i18next'
import { Trans } from 'react-i18next'
import { useChangeDemandCompletionMutation, useDeleteDemandMutation } from '../../store/redux/reducers/demandSlice'
import { generatePath, useNavigate } from 'react-router-dom'
import navigation from '../../config/navigation'
import ConfirmButton from '../Button/ConfirmButton'

type DemandActionMenuProps = {
  demandId: string
  isLeader: boolean
  isMyDemand: boolean
  isCompleted: boolean
  isOnlyRecruiter: boolean
}

const DemandActionMenu = ({ demandId, isLeader, isMyDemand, isCompleted, isOnlyRecruiter }: DemandActionMenuProps) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
  const [deleteDemand] = useDeleteDemandMutation()
  const [changeCompleted] = useChangeDemandCompletionMutation()
  const { displayNotification } = useNotification()
  const { setIsEditMode } = useEditMode()

  const handleDeleteCost = async (popupState: any) => {
    if (demandId === null) {
      return
    }
    try {
      await deleteDemand(demandId).unwrap()
      displayNotification({ message: t('page.demands.alert.demandDeleted'), type: 'success' })
    } catch (_) {
      displayNotification({ message: t('page.demands.alert.demandDeleteError'), type: 'error' })
    }
    setOpenDeleteDialog(false)
    popupState.close()
  }

  const handleChangeCompleted = async () => {
    if (demandId === null) {
      return
    }
    try {
      await changeCompleted(demandId).unwrap()
      displayNotification({ message: t('page.demands.alert.demandCompletedChange'), type: 'success' })
    } catch (_) {
      displayNotification({ message: t('page.demands.alert.demandCompletedError'), type: 'error' })
    }
  }

  return (
    <PopupState
      popupId={`demand-action-menu-${demandId}`}
      variant={'popover'}
      parentPopupState={null}
      disableAutoFocus={null}
    >
      {popupState => (
        <React.Fragment>
          <IconButton
            disabled={(isLeader && !isMyDemand) || isOnlyRecruiter}
            {...bindTrigger(popupState)}
            sx={{ color: 'inherit' }}
            aria-label={'demand-action-menu-button'}
          >
            <MoreVert />
          </IconButton>
          <StyledMenu sx={{ '& .MuiPaper-root': { minWidth: '50px' } }} {...bindMenu(popupState)}>
            <MenuItem onClick={() => {
                setIsEditMode(true)
                navigate(generatePath(navigation.editDemand, { id: demandId }))
              }}
            >
              <ListItemIcon>
                <BorderColor />
              </ListItemIcon>
              <ListItemText>{t('page.demands.editDemand')}</ListItemText>
            </MenuItem>
            <MenuItem
              disableRipple
              sx={{ color: 'text.main' }}
              onClick={e => {
                e.stopPropagation()
                handleChangeCompleted()
              }}
            >
              <FormControlLabel
                control={<Switch size="small" checked={isCompleted} sx={{ mr: 0.75 }} />}
                label={t('components.toggle.isCompleted')}
              />
            </MenuItem>
            <MenuItem onClick={() => setOpenDeleteDialog(true)}>
              <ListItemIcon>
                <Delete />
              </ListItemIcon>
              <ListItemText>{t('page.demands.deleteDemand')}</ListItemText>
            </MenuItem>
          </StyledMenu>
          <Dialog
            onClose={() => {setOpenDeleteDialog(false)}}
            open={openDeleteDialog}
            PaperProps={{
              style: {
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(2),
                paddingTop: theme.spacing(1),
                paddingBottom: theme.spacing(2),
              },
            }}
          >
            <DialogTitle>{t('page.demands.dialog.delete.title')}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                <Trans i18nKey="page.demands.dialog.delete.content" values={{ name: demandId }} />
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ gap: 2 }}>
              <Button size="small" onClick={() => setOpenDeleteDialog(false)}>{t('page.adminsPage.dialog.return')}</Button>
              <ConfirmButton text={t('page.projects.dialog.delete.confirm')} onClick={() => handleDeleteCost(popupState)}/>
            </DialogActions>
          </Dialog>
        </React.Fragment>
      )}
    </PopupState>
  )
}

export default DemandActionMenu